/**
 * TanStack Query hooks — Extracted Fields (override humain).
 *
 * Route API consommée : PATCH /v1/extracted-fields/:id
 * Schema partagé : `OverrideExtractedFieldSchema` dans @kollectivia/shared
 * (9.T.5.v3.3 — audit PO Q3 : source unique).
 *
 * 9.C scope : on n'expose que `value`, `unit`, `needs_review`, `note`.
 * `confidence`, `citation_quote`, `citation_page` restent figés (D38 — édition
 * de citation reportée). `confidence` est une métrique IA, pas un input humain.
 *
 * 9.T.5.v3.3 (audit PO Q5 hard-break) : la variable `submissionId` est
 * REMPLACÉE par `{ parentKind, parentId }` — signature parent-agnostique.
 * Aucun wrapper backward-compat submission-only n'est maintenu.
 *
 * Invalidations onSuccess dispatch par parentKind :
 *   - `extractionsKeys.fields(extractionId)` (la liste éditée)
 *   - `["submissions", "detail", parentId]` OU `["intake-events", "detail", parentId]`
 */

import type {
  ExtractedFieldRow,
  ExtractionParentKind,
  OverrideExtractedFieldInput,
} from "@kollectivia/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

import { extractionsKeys } from "./extractions";

// Re-export types shared pour éviter aux consumers d'importer @kollectivia/shared.
export type { OverrideExtractedFieldInput } from "@kollectivia/shared";

export type OverrideExtractedFieldVars = {
  /** id de la row `app.extracted_fields` */
  fieldId: string;
  /** id de l'extraction parente — sert à invalider la liste affichée */
  extractionId: string;
  /**
   * Discriminant parent (9.T.5.v3.3) — dispatche l'invalidation vers la
   * clé `submissions.detail` OR `intake-events.detail` selon la source.
   */
  parentKind: ExtractionParentKind;
  /** id du parent (submission_id OU intake_event_id) */
  parentId: string;
  input: OverrideExtractedFieldInput;
};

export function useOverrideExtractedField() {
  const qc = useQueryClient();
  return useMutation<ExtractedFieldRow, Error, OverrideExtractedFieldVars>({
    mutationFn: ({ fieldId, input }) =>
      api.patch<ExtractedFieldRow>(
        `/v1/extracted-fields/${fieldId}`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_row, vars) => {
      void qc.invalidateQueries({
        queryKey: extractionsKeys.fields(vars.extractionId),
      });
      void qc.invalidateQueries({
        queryKey: extractionsKeys.byParent(vars.parentKind, vars.parentId),
      });
      const detailKey =
        vars.parentKind === "submission"
          ? ["submissions", "detail", vars.parentId]
          : ["intake-events", "detail", vars.parentId];
      void qc.invalidateQueries({ queryKey: detailKey });
    },
  });
}
